import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { ThemeContext } from 'styled-components'
import { Helmet } from 'react-helmet'
import Head from 'next/head'

import ButtonRounded from 'components/ButtonRounded'
import { RU, UK, IE, NL, BE } from 'constants/countries'
import { DELIVERY_TYPES } from 'constants/sales'
import { getTrackingCode } from 'constants/retargeting'
import { DEVELOPMENT } from 'constants/environments'
import { genCityAdsCartPage } from 'constants/cityAdsScripts'
import { LayoutContext } from 'contexts/Layout'
import { LocalizationContext } from 'contexts/Localization'
import { MenuMainContext } from 'contexts/MenuMain'
import { ResizeContext } from 'contexts/Resize'
import { cartCheckout, cart as cartRoute } from 'routes'
import { BoxBase, Col } from 'styles/components/layout'
import { H3Like } from 'styles/components/typography'
import { gtmEvent } from 'utils/client/analitics'
import { getCartToCityAdsVariable} from 'utils/city-ads'

import CartDeliveryInfo from './components/CartDeliveryInfo'
import CartDeliveryInfoNew from './components/CartDeliveryInfoNew'
import CartDisclaimer from './components/CartDisclaimer'
import CartEmptyNew from './components/CartEmptyNew'
import CartFillForm from './components/CartFillForm'
import CartFormCommon from './components/CartFormCommon'
import CartGoodsBlock from './components/CartGoodsBlock'
import CartGoodsBlockNew from './components/CartGoodsBlockNew'
import CartHeader from './components/CartHeader'
import CartRussiaRedesign from './components/CartRussiaRedesign'
import {
  DisclaimerBox,
  GoodsBlock,
  FormBox,
  FillFormBox,
  RowStyled,
  ButtonContent,
  ButtonContentMobile,
  ButtonRoundedStyled,
  BannerIndent,
} from './styles'

const Cart = props => {
  const goodsBlock = React.useRef(null)
  const formBlock = React.useRef(null)
  const [isGtmSent, setIsGtmSent] = React.useState(false)
  const { dict, sales, locale } = React.useContext(LocalizationContext)
  const { desktop, scaling } = useContext(ResizeContext)
  const { colors } = useContext(ThemeContext)
  const { setFooterBorder } = useContext(LayoutContext)
  const { isSalesBanner, salesBannerHeight, activeBanner } = useContext(
    MenuMainContext,
  )
  const {
    texts,
    cart,
    personalInfoChange,
    resetPromo,
    isCartInitOrUpdate,
    changeDeliveryType,
    addAmount,
    sendPromo,
    cartPageLeaving,
    privacy,
    terms,
    essential_points,
  } = props
  const summary = cart.sum
  const isCartEmpty = summary.amount === 0
  /*
   * Callbacks to manage cart's state
   */
  const decreaseGoods = React.useCallback(
    ({ isSubscription, productType, num = 1 }) => {
      const { amount } = cart.goods[productType]

      if (amount > 0) {
        addAmount({
          productType,
          amount: -num,
          isSubscription,
        })
      }
    },
    [cart.goods, addAmount],
  )

  const increaseGoods = React.useCallback(
    ({ isSubscription, productType, num = 1 }) => {
      addAmount({
        productType,
        amount: num,
        isSubscription,
      })
    },
    [addAmount],
  )

  const toggleSubscription = React.useCallback(
    ({ isSubscription, productType }) => {
      addAmount({
        amount: 1,
        productType,
        isSubscription,
      })
    },
    [addAmount],
  )

  const submitPromocode = React.useCallback(
    ({ promocode }) => {
      sendPromo({
        promocode: promocode ? promocode.trim() : promocode,
        locale,
      })
    },
    [locale, sendPromo],
  )

  /*
   * Perform state manipulations when user leaves cart page
   * for example:
   * - terminate order if user leave in the middle of process
   */
  React.useEffect(() => {
    return () => {
      cartPageLeaving()
    }
  }, [cartPageLeaving])

  const isEU = React.useMemo(() => [UK, IE, NL, BE].includes(locale), [locale])
  const isRU = React.useMemo(() => locale === RU, [locale])

  React.useEffect(() => {
    if (isEU) setFooterBorder(colors.white)

    return () => {
      if (isEU) setFooterBorder(null)
    }
  }, [colors.white, setFooterBorder, isEU])

  React.useEffect(() => {
    if (!isCartInitOrUpdate && !isGtmSent) {
      setIsGtmSent(true)
      if (cart.sum.amount) {
        const goods = Object.keys(cart.goods).reduce((acc, curr) => {
          acc[curr] = cart.goods[curr].amount
          return acc
        }, {})

        gtmEvent({
          event: 'user opened cart with items',
          description: JSON.stringify(goods),
        })
      } else
        gtmEvent({
          event: 'user opened cart without items',
        })
    }
  }, [isCartInitOrUpdate, cart, isGtmSent])

  const goodsForCityAds = getCartToCityAdsVariable(cart.goods);
  const cityAddsCartScript = genCityAdsCartPage(goodsForCityAds.products, goodsForCityAds.count);
  const server = process.env.server

  return (
    <>
      {server.MERCHANT_ID && (
        <Head>
          <script
            type="text/javascript"
            dangerouslySetInnerHTML={{ __html: getTrackingCode(cartRoute.basePath, server.MERCHANT_ID) }}
          />
        </Head>
      )}

      {(process.env.NODE_ENV !== DEVELOPMENT && locale === RU)
        && <Helmet><script id='xcntmyAsync' type='text/javascript'>{cityAddsCartScript}</script></Helmet>
      }

      {isSalesBanner && (
        <BannerIndent
          indent={
            activeBanner?.isTimer ? { md: 50, lg: 50 } : salesBannerHeight
          }
        />
      )}
      {isRU ? (
        <CartRussiaRedesign
          {...{
            texts,
            cart,
            isCartInitOrUpdate,
            decreaseGoods,
            increaseGoods,
            submitPromocode,
            closePromocode: resetPromo,
            personalInfoChange,
            privacy,
            terms,
            essential_points,
          }}
        />
      ) : (
        <>
      {isEU && !isCartEmpty ? null : (
        <CartHeader
          isCartEmpty={isCartEmpty}
          sections={dict.sections}
          texts={texts}
          goodsBlockRef={goodsBlock}
          isLessInfo={isEU}
          // || isRU}
        />
      )}

      <GoodsBlock ref={goodsBlock}>
        {!isEU ? (
          <CartGoodsBlock
            {...{
              texts,
              cart,
              isCartInitOrUpdate,
              decreaseGoods,
              increaseGoods,
              toggleSubscription,
              submitPromocode,
              closePromocode: resetPromo,
              changeDeliveryType,
            }}
          />
        ) : isCartEmpty ? (
          <CartEmptyNew
            {...{
              texts,
              cart,
              isCartInitOrUpdate,
              increaseGoods,
            }}
          />
        ) : (
          <CartGoodsBlockNew
            {...{
              texts,
              cart,
              isCartInitOrUpdate,
              decreaseGoods,
              increaseGoods,
              toggleSubscription,
              submitPromocode,
              closePromocode: resetPromo,
            }}
          />
        )}
      </GoodsBlock>

      {!isCartEmpty &&
        (cart.isSinglePage ? (
          <>
            <FillFormBox>
              <CartFillForm title={texts.form.title} formBlockRef={formBlock} />
            </FillFormBox>

            <div ref={formBlock}>
              <FormBox>
                <CartFormCommon
                  {...{
                    dict,
                    texts,
                    rules: { essential_points, terms, privacy },
                    cart,
                    personalInfoChange,
                  }}
                />
              </FormBox>

              {cart.deliveryType === DELIVERY_TYPES.courier && locale !== RU && (
                <CartDeliveryInfo
                  isLoading={isCartInitOrUpdate}
                  deliveryConfig={sales.delivery}
                  deliveryTexts={texts.delivery}
                />
              )}
            </div>
          </>
        ) : (
          !isEU && (
            <BoxBase>
              <RowStyled>
                <Col width={{ xs: 1, md: 6 / 12, lg: 4 / 12 }}>
                  {desktop || scaling ? (
                    <ButtonRounded
                      as="Link"
                      asPath={cartCheckout.as(locale)}
                      to={cartCheckout.link(locale)}
                      size="large"
                    >
                      <ButtonContent>
                        <H3Like
                          dangerouslySetInnerHTML={{
                            __html: dict.buttons.fill_form,
                          }}
                        />
                      </ButtonContent>
                    </ButtonRounded>
                  ) : (
                    <ButtonRoundedStyled
                      forwardedAs="Link"
                      asPath={cartCheckout.as(locale)}
                      to={cartCheckout.link(locale)}
                    >
                      <ButtonContentMobile>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: dict.buttons.fill_form,
                          }}
                        />
                      </ButtonContentMobile>
                    </ButtonRoundedStyled>
                  )}
                </Col>
              </RowStyled>
            </BoxBase>
          )
        ))}

      {isEU && !isCartEmpty ? (
        <CartDeliveryInfoNew
          {...{
            info: texts.info,
            disclaimer: texts.disclaimer,
            isCartInitOrUpdate,
            deliveryConfig: sales.delivery,
          }}
        />
      ) : (
        <DisclaimerBox>
          <CartDisclaimer
            content={texts.disclaimer}
            isWide={!cart.isSinglePage}
          />
        </DisclaimerBox>
      )}
        </>
      )}
    </>
  )
}

Cart.propTypes = {
  addAmount: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  isCartInitOrUpdate: PropTypes.bool.isRequired,
  personalInfoChange: PropTypes.func.isRequired,
  resetPromo: PropTypes.func.isRequired,
  sendPromo: PropTypes.func.isRequired,
  cartPageLeaving: PropTypes.func.isRequired,
  texts: PropTypes.object.isRequired,
  terms: PropTypes.shape({
    title: PropTypes.string,
    list: PropTypes.array,
  }),
  essential_points: PropTypes.shape({
    title: PropTypes.string,
    list: PropTypes.array,
  }),
  privacy: PropTypes.shape({
    title: PropTypes.string,
    list: PropTypes.array,
  }),
  changeDeliveryType: PropTypes.func,
}

export default Cart
